import axios, {AxiosError} from 'axios';
import type {AxiosInstance, AxiosRequestConfig, AxiosResponse, RawAxiosRequestHeaders} from 'axios';
import {RequestError} from './errors';
import {
    benefitInterceptor,
    pointsInsufficientInterceptor,
    permissionInterceptor,
    unauthorizedInterceptor,
} from './interceptors';
import type {
    AfterResponseHook,
    BeforeRequestHook,
    CreateRequestConfig,
    InternalRequestConfig,
    RequestInstance,
    RequestOptions,
    RequestRetryOptions,
} from './types';
import message from '@/components/common/Message';
import {ERROR_MESSAGES, HTTP_STATUS, STATUS_CODE} from '@/constants';
import type {ApiResponse} from '@/types';

const DEFAULT_TIMEOUT = 10_000;
const REQUEST_TIMEOUT_TOAST_KEY = 'request-timeout';
const DEFAULT_RETRY: Required<RequestRetryOptions> = {
    limit: 2,
    methods: ['get'],
    statusCodes: [
        HTTP_STATUS.REQUEST_TIMEOUT,
        HTTP_STATUS.SERVER_ERROR,
        HTTP_STATUS.BAD_GATEWAY,
        HTTP_STATUS.SERVICE_UNAVAILABLE,
        HTTP_STATUS.GATEWAY_TIMEOUT,
    ],
};

type RequestMethod = 'get' | 'post' | 'put' | 'delete' | 'patch';

function resolveRetryOptions(
    retry?: RequestRetryOptions | false,
): Required<RequestRetryOptions> | false {
    if (retry === false) return false;
    return {
        ...DEFAULT_RETRY,
        ...retry,
        methods: retry?.methods ?? DEFAULT_RETRY.methods,
        statusCodes: retry?.statusCodes ?? DEFAULT_RETRY.statusCodes,
    };
}

function resolveWithCredentials(credentials?: RequestCredentials): boolean {
    return credentials !== 'omit';
}

function toSearchParams(params?: RequestOptions['params']): URLSearchParams {
    if (!params) return new URLSearchParams();
    if (params instanceof URLSearchParams) return params;
    if (typeof params === 'string') return new URLSearchParams(params);

    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value == null) continue;
        const values = Array.isArray(value) ? value : [value];
        for (const item of values) {
            if (item == null) continue;
            searchParams.append(
                key,
                typeof item === 'object' ? JSON.stringify(item) : String(item),
            );
        }
    }
    return searchParams;
}

function toAxiosHeaders(headers?: RequestOptions['headers']): RawAxiosRequestHeaders {
    if (!headers) return {};
    if (!(headers instanceof Headers)) return headers;

    const result: RawAxiosRequestHeaders = {};
    headers.forEach((value, key) => {
        result[key] = value;
    });
    return result;
}

function shouldRetryResponse(
    response: AxiosResponse,
    attempt: number,
    limit: number,
    retry: Required<RequestRetryOptions> | false,
): boolean {
    if (attempt >= limit || retry === false) return false;
    return retry.statusCodes.includes(response.status);
}

async function requestWithRetry<T>(
    instance: AxiosInstance,
    config: AxiosRequestConfig,
    retry: Required<RequestRetryOptions> | false,
): Promise<AxiosResponse<T>> {
    const method = String(config.method ?? 'get').toLowerCase();
    const canRetry = retry !== false && retry.methods.includes(method);
    const limit = canRetry ? retry.limit : 0;
    let lastError: unknown = null;
    let lastResponse: AxiosResponse<T> | null = null;

    for (let attempt = 0; attempt <= limit; attempt++) {
        try {
            const response = await instance.request<T>(config);
            lastResponse = response;
            if (shouldRetryResponse(response, attempt, limit, retry)) continue;
            return response;
        } catch (error) {
            lastError = error;
            if (!canRetry || attempt >= limit) throw error;
        }
    }

    if (lastResponse) return lastResponse;
    if (lastError instanceof Error) throw lastError;
    throw new Error(ERROR_MESSAGES.DEFAULT_ERROR_MESSAGE);
}

async function runBeforeRequestHooks(
    config: InternalRequestConfig,
    registeredHooks: Set<BeforeRequestHook>,
    customHooks?: BeforeRequestHook[],
): Promise<void> {
    for (const hook of registeredHooks) {
        await hook(config);
    }
    for (const hook of customHooks ?? []) {
        await hook(config);
    }
}

async function runAfterResponseHooks(
    response: AxiosResponse,
    options: RequestOptions,
    registeredHooks: Set<AfterResponseHook>,
    customHooks?: AfterResponseHook[],
): Promise<void> {
    for (const hook of registeredHooks) {
        await hook(response, options);
    }
    for (const hook of customHooks ?? []) {
        await hook(response, options);
    }
}

function throwForHttpError(response: AxiosResponse): void {
    if (response.status >= 200 && response.status < 300) return;
    throw new AxiosError(
        `Request failed with status code ${response.status}`,
        AxiosError.ERR_BAD_RESPONSE,
        response.config,
        response.request,
        response,
    );
}

function isTimeoutError(error: unknown): boolean {
    return (
        axios.isAxiosError(error) &&
        (error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ETIMEDOUT)
    );
}

function showTimeoutToast(): void {
    message.error({
        content: ERROR_MESSAGES.REQUEST_TIMEOUT_MESSAGE,
        key: REQUEST_TIMEOUT_TOAST_KEY,
    });
}

function isAcsTokenStatus(status: number): boolean {
    return (
        status === STATUS_CODE.ACS_TOKEN_RATE_LIMITED ||
        status === STATUS_CODE.ACS_TOKEN_ACCOUNT_BLOCKED ||
        status === STATUS_CODE.ACS_TOKEN_CAPTCHA_REQUIRED ||
        status === STATUS_CODE.ACS_TOKEN_RISK_BLOCKED
    );
}

function unwrapApiResponse<T>(response: AxiosResponse<ApiResponse<T>>, silent?: boolean): T {
    const res = response.data as ApiResponse<T> | null | undefined;
    if (!res || typeof res.status !== 'number') {
        const msg = ERROR_MESSAGES.DEFAULT_ERROR_MESSAGE;
        if (!silent) message.error(msg);
        throw new RequestError(msg, -1);
    }
    if (res.status !== 0) {
        if (isAcsTokenStatus(res.status)) {
            const msg = res.message || ERROR_MESSAGES.ACS_TOKEN_STATUS_MESSAGE;
            throw new RequestError(msg, res.status);
        }
        const msg = res.message || ERROR_MESSAGES.DEFAULT_ERROR_MESSAGE;
        if (!silent) message.error(msg);
        throw new RequestError(msg, res.status);
    }
    return res.data;
}

export function createRequest(config?: CreateRequestConfig): RequestInstance {
    const beforeRequestHooks = new Set<BeforeRequestHook>();
    const afterResponseHooks = new Set<AfterResponseHook>();

    // 注册内置拦截器
    afterResponseHooks.add(unauthorizedInterceptor);
    afterResponseHooks.add(permissionInterceptor);
    afterResponseHooks.add(benefitInterceptor);
    afterResponseHooks.add(pointsInsufficientInterceptor);

    const {
        baseURL,
        credentials,
        hooks: customHooks,
        prefixUrl,
        retry,
        timeout,
        ...restConfig
    } = config ?? {};
    const retryOptions = resolveRetryOptions(retry);
    const defaultBaseURL = import.meta.env.VITE_API_BASE_URL || '/';

    const instance = axios.create({
        baseURL: baseURL ?? prefixUrl ?? defaultBaseURL,
        timeout: timeout ?? DEFAULT_TIMEOUT,
        validateStatus: () => true,
        withCredentials: resolveWithCredentials(credentials),
        ...restConfig,
    });

    const request = async <T = unknown>(
        method: RequestMethod,
        url: string,
        data?: object | FormData,
        options?: RequestOptions,
    ): Promise<T> => {
        const {
            credentials: requestCredentials,
            headers,
            params,
            silent,
            acsToken,
            timeoutToast,
            retry,
            ...axiosOptions
        } = options ?? {};
        const requestConfig: InternalRequestConfig = {
            ...axiosOptions,
            data: data ?? undefined,
            headers: toAxiosHeaders(headers),
            method,
            params: toSearchParams(params),
            url,
            validateStatus: () => true,
            acsToken,
            withCredentials: resolveWithCredentials(requestCredentials ?? credentials),
        };

        await runBeforeRequestHooks(requestConfig, beforeRequestHooks, customHooks?.beforeRequest);
        delete requestConfig.acsToken;

        // 优先取运行时动态的retry
        const requestRetryOptions = retry === undefined ? retryOptions : resolveRetryOptions(retry);

        const response = await requestWithRetry<ApiResponse<T>>(
            instance,
            requestConfig,
            requestRetryOptions,
        ).catch((error: unknown) => {
            if (isTimeoutError(error) && timeoutToast !== false) {
                showTimeoutToast();
            }
            throw error;
        });
        await runAfterResponseHooks(
            response,
            options ?? {},
            afterResponseHooks,
            customHooks?.afterResponse,
        );

        throwForHttpError(response);
        return unwrapApiResponse(response, silent ?? true);
    };

    return {
        get: (url, options) => request('get', url, undefined, options),
        post: (url, data, options) => request('post', url, data, options),
        put: (url, data, options) => request('put', url, data, options),
        delete: (url, data, options) => request('delete', url, data, options),
        patch: (url, data, options) => request('patch', url, data, options),
        // 注册请求前钩子，返回取消注册函数
        useBeforeRequest(hook: BeforeRequestHook) {
            beforeRequestHooks.add(hook);
            return () => {
                beforeRequestHooks.delete(hook);
            };
        },
        // 注册响应后钩子，返回取消注册函数
        useAfterResponse(hook: AfterResponseHook) {
            afterResponseHooks.add(hook);
            return () => {
                afterResponseHooks.delete(hook);
            };
        },
    };
}
